import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Select from '@/src/components/common/Select/Select';
import { checkIfAdmin } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import { useGetEmailFromOptions } from '../../api/getEmailFromOptions';
import InputWithLabelLayout from '../ComposeEmailForm/Fields/InputWithLabelLayout';
import { useGetInboxAccess } from '../Header/api/inboxAccess';

const checkIsUserRoleFullAccess = (accessUserList, userId, loggedUser) => {
  const currentUserAccess = accessUserList.find((userAccess) => userAccess.id === userId);
  const isAdmin = checkIfAdmin(loggedUser);

  return isAdmin || (currentUserAccess && currentUserAccess.level === 3);
};

type FromInputProps = {
  projectId: string;
  setFromEmail: (recipients: { value: string; label: string }) => void;
  fromEmail: { value: string; label: string };
  fieldLabel: string;
  isRequired?: boolean;
  LastComponentSlot?: React.ReactNode;
  onBlur?: (value: string, tags: any[]) => void;
};
const FromInput = ({
  fieldLabel,
  LastComponentSlot = null,
  isRequired = false,
  fromEmail,
  setFromEmail,
}: FromInputProps) => {
  const { project, loggedUser } = useSelector((state: RootState) => ({
    project: state.project,
    loggedUser: state.loggedUser.data,
  }));

  const [fromOptions, setFromOptions] = useState<{ value: string; label: string }[]>([]);

  const inboxAccessQuery = useGetInboxAccess({ projectId: project.Selected.id });
  const inboxEmailFromOptionsQuery = useGetEmailFromOptions({ projectId: project.Selected.id });
  const currentUserFullName = `${loggedUser.firstName} ${loggedUser.lastName}`;

  useEffect(() => {
    if (!inboxAccessQuery.data || !inboxEmailFromOptionsQuery.data) return;

    const currentUserId = Number(loggedUser.id);

    const hasFullAccess = checkIsUserRoleFullAccess(
      inboxAccessQuery.data.data.accessUserlist,
      currentUserId,
      loggedUser
    );

    const workstreamEmailsFromOptions = inboxEmailFromOptionsQuery.data.workstreamEmails.map(
      (email) => ({
        value: email,
        label: email,
      })
    );

    let options = [...workstreamEmailsFromOptions];

    if (hasFullAccess) {
      const projectEmailOptions = {
        label: inboxEmailFromOptionsQuery.data.projectEmail,
        value: inboxEmailFromOptionsQuery.data.projectEmail,
      };
      options = [projectEmailOptions, ...workstreamEmailsFromOptions];
      setFromEmail(projectEmailOptions);
    } else if (workstreamEmailsFromOptions.length === 1) {
      setFromEmail(options[0]);
    }

    setFromOptions(options);
  }, [
    inboxAccessQuery.data,
    inboxEmailFromOptionsQuery.data,
    loggedUser,
    project,
    project.Selected.composeEmailFromOptions,
    setFromEmail,
  ]);

  return (
    <InputWithLabelLayout
      label={fieldLabel}
      isRequired={isRequired}
      LastComponentSlot={LastComponentSlot}
    >
      <Select
        selected={fromEmail}
        options={fromOptions}
        isSearchable={true}
        onChange={setFromEmail}
        showActiveIcon={true}
        classes={{
          main: 'commonSelectOption !tw-h-[30px]',
          trigger: '!tw-h-[30px]',
        }}
        renderSelected={(selected: { value: string; label: string }) => (
          <div className="tw-flex tw-gap-[5px] tw-w-full">
            <span className="tw-font-brandonTextMedium">{currentUserFullName}</span>
            <span>{`<${selected.label}>`}</span>
          </div>
        )}
      />
    </InputWithLabelLayout>
  );
};

export default FromInput;
